/* About page — two reference behaviours, dependency-free:

   1. Flick-through image ([data-flick-wrap] > [data-flick-img]): on desktop,
      hovering cycles to the next photo immediately and then every 300ms
      (the reference's exact rhythm, ~3.3/s); on touch/small screens it
      auto-cycles continuously at the same 300ms pace (Mats: same speed,
      always on — no hover on touch).

   2. Floating marks ([data-mark-flick] > [data-mark]): as the section
      scrolls through the viewport, a doubled sequence [0,1,2,0,1,2] plays
      over the scroll progress — you flick through the marks twice per pass
      (the reference's GSAP scrub, ported to a scroll handler). Plus the
      stage's vertical scroll-drift (their second scrubbed timeline).

   Registry-managed: re-inits after every Barba swap; intervals, rAF loops and
   listeners all die with the page's abort signal. */
(() => {
  const finePointer = window.matchMedia('(min-width: 992px) and (pointer: fine)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const init = (ctx) => {
    const signal = ctx && ctx.signal;
    const aborted = () => !!(signal && signal.aborted);

    /* ---------- flick-through ----------
       Desktop (fine pointer): flicks only while hovered. Touch/mobile: the visitor advances with a button. Resize stops any
       active desktop hover cycle; touch never starts an interval. */
    document.querySelectorAll('[data-flick-wrap]').forEach((wrap) => {
      const imgs = [...wrap.querySelectorAll('[data-flick-img]')];
      if (imgs.length < 2) return;
      wrap.classList.add('is-ready');

      let index = 0;
      let interval = null;

      const next = () => {
        imgs[index].classList.remove('show');
        index = (index + 1) % imgs.length;
        imgs[index].classList.add('show');
      };

      const start = () => {
        if (!interval) interval = setInterval(next, 300);
      };
      const stop = () => {
        clearInterval(interval);
        interval = null;
      };

      if (signal) signal.addEventListener('abort', stop);

      // Both stay still until the visitor interacts.
      const apply = () => stop();
      const nextButton = wrap.querySelector('[data-flick-next]');
      if (nextButton) nextButton.addEventListener('click', next, { signal });

      wrap.addEventListener('mouseenter', () => {
        if (!finePointer.matches || reduceMotion) return;
        next(); // change immediately, then keep flicking
        start();
      }, { signal });
      wrap.addEventListener('mouseleave', () => {
        if (finePointer.matches) stop();
      }, { signal });

      apply();
      finePointer.addEventListener('change', apply, { signal });
    });

    /* ---------- scroll-flicked marks ---------- */
    const flickSections = [...document.querySelectorAll('[data-mark-flick]')];
    if (!flickSections.length || reduceMotion) return;

    let ticking = false;

    const update = () => {
      ticking = false;
      const vh = window.innerHeight;

      flickSections.forEach((section) => {
        const marks = [...section.querySelectorAll('[data-mark]')];
        if (marks.length < 2) return;

        const rect = section.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return;

        // 0 → 1 while the section passes through the viewport
        const progress = Math.max(0, Math.min(1, (vh - rect.top) / (vh + rect.height)));

        // doubled sequence: run the marks twice over the full pass
        const steps = marks.length * 2;
        const idx = Math.min(steps - 1, Math.floor(progress * steps)) % marks.length;

        marks.forEach((mark, i) => {
          mark.classList.toggle('show', i === idx);
        });
      });
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true, signal });
    window.addEventListener('resize', onScroll, { passive: true, signal });
    update();

    /* ---------- vertical drift of the mark stage (desktop) ----------
       The reference stacks a SECOND scrubbed timeline on the same icon: the
       whole stage glides from yPercent -30 to +15 as it passes through the
       viewport, with scrub: 1 — a ~1s smoothing so it drifts and lags rather
       than sticking to the scrollbar. Imitated with a per-frame lerp toward the
       scroll-derived target. We measure the untransformed wrapper (never the
       stage itself) so the transform we apply can't feed back into the reading.
       Desktop/fine-pointer only, matching the reference; on touch the stage
       keeps its static CSS position. */
    if (!finePointer.matches) return;

    const drifters = flickSections
      .map((section) => ({ wrap: section, stage: section.querySelector('.aduo_marks-stage') }))
      .filter((o) => o.stage);
    if (!drifters.length) return;

    const DRIFT_LERP = 0.06; // ~ the reference's scrub: 1 feel
    const state = drifters.map(() => ({ current: null, applied: null }));

    const driftLoop = () => {
      if (aborted()) return; // the page this loop belongs to is gone
      const vh = window.innerHeight;

      drifters.forEach(({ wrap, stage }, i) => {
        const rect = wrap.getBoundingClientRect();
        // start: wrap top at viewport bottom → end: wrap bottom 25% above the top
        const denom = 1.25 * vh + rect.height;
        const p = Math.max(0, Math.min(1, (vh - rect.top) / denom));
        // end nudged from +15 to +24 (Mats: ride a touch further down before leaving)
        const target = -30 + p * 54; // yPercent -30 → +24

        const s = state[i];
        s.current = s.current === null ? target : s.current + (target - s.current) * DRIFT_LERP;

        const rounded = Math.round(s.current * 100) / 100;
        if (rounded !== s.applied) {
          stage.style.transform = `translateY(${rounded}%)`;
          s.applied = rounded;
        }
      });

      requestAnimationFrame(driftLoop);
    };

    driftLoop();
  };

  if (window.Onni && window.Onni.register) {
    window.Onni.register('about-flick', init);
  } else {
    init();
  }
})();
